import * as React from "react";
import Typography from "@mui/material/Typography";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme, styled } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import ProjectService from "../api/project";
import CustomTextField from "../common/CustomTextField";
import { getUrlVars } from "../utils/systemInfo";
import Breadcrumbs from "../common/breadcrumbs";
import Swal from "sweetalert2";

const breadcrumbs = [
    { id: 0, name: "Home", href: "./" },
    { id: 1, name: "MachineStatus", href: "machineStatus" },
    { id: 2, name: "AddMachineStatus", href: "AddMachineStatus" },
];

const StyledLoadingButton = styled((props) => <LoadingButton {...props} />)(
    ({ theme }) => ({
        backgroundColor: theme.palette.custom.buttonColor,
        width: "10%",
        "&.MuiLoadingButton-loading": {
            backgroundColor: theme.palette.custom.light,
        },
    })
);

const StyledButton = styled((props) => <Button {...props} />)(({ theme }) => ({
    backgroundColor: theme.palette.custom.borderColor,
    width: "10%",
    color: theme.palette.custom.text,
    "&:hover": {
        backgroundColor: theme.palette.custom.buttonHoverColor,
        color: theme.palette.custom.buttonHoverTextColor,
        boxShadow: "none",
    },
}));

function AddMachineStatusPage() {
    const { t } = useTranslation();
    const theme = useTheme();
    const [serial, setSerial] = useState("");
    const [savingLoading, setSavingLoading] = useState(false);
    const organziation = useSelector(({ organziation }) => organziation);
    const navigate = useNavigate();
    // eslint-disable-next-line no-unused-vars
    const [commissioningSignoffFile, setCommissioningSignoffFile] =
        useState(null);
    const [commissioningSignoffBase64, setCommissioningSignoffBase64] =
        useState("");
    const [groupId, setGroupId] = useState("");
    const [name, setName] = useState("");
    const orgObj =
        organziation ?? JSON.parse(localStorage.getItem("defaultOrganization"));
    const apiKey = orgObj?.api_key_list?.[0]?.key;
    const [serialParamID, setSerialParamID] = useState("");

    useEffect(() => {
        const projectId = String(getUrlVars()["projectId"]);
        const groupId = String(getUrlVars()["groupId"]);
        if (!projectId || !groupId || !apiKey) return;
        ProjectService.GetProjectSummary(projectId, "", "active", apiKey).then(
            (jsonSummary) => {
                if (jsonSummary.count > 0) {
                    const group = jsonSummary.project_summary.find(
                        (g) => g.task_group_id === groupId
                    );
                    if (group) {
                        setGroupId(group.task_group_id);
                        setName(group.name);
                        const infoTask = group.tasks?.find((t) => t.name === "Info");
                        if (infoTask) {
                            const serialParam = infoTask.params?.find(
                                (p) => p.name === "serial"
                            );
                            setSerial(serialParam?.value || "");
                            setSerialParamID(serialParam?.task_param_id);
                        } else {
                            setSerial("");
                            setSerialParamID("");
                        }
                    }
                }
            }
        );
    }, [apiKey]);

    async function createTasks(task_id, orgID, apiKey) {
        let Tasks = [];
        switch (orgID.toString()) {
            case "18":
                Tasks = [
                    ["MCI Installed", "16"],
                    ["TMM Received for Commissioning", "16"],
                    ["Brake Control", "16"],
                    ["Throttle Control", "16"],
                    ["Cold Commissioning", "16"],
                    ["L9 Activation", "16"],
                    ["SIMAC Activation", "16"],
                ];
                break;
            case "65":
                Tasks = [
                    ["Firmware Updated", "16"],
                    ["Wifi Script", "16"],
                    ["SIMAC Check", "16"],
                ];
                break;
            case "30":
                Tasks = [
                    ["Delivered", "16"],
                    ["MCI Installed", "16"],
                    ["SBI", "16"],
                    ["SIMAC", "16"],
                    ["L9 Activation", "16"],
                ];
                break;
            case "102":
                Tasks = [
                    ["Milestone 1 MCI Installed (10%)", "16"],
                    ["Milestone 2 SBI (50%)", "16"],
                    ["Milestone 3 SIMAC (80%)", "16"],
                    ["Milestone 4 L9 Activation (100%)", "16"],
                ];
                break;
            case "84":
                Tasks = [
                    ["Firmware Updated (30%)", "16"],
                    ["Wifi Script (60%)", "16"],
                    ["SIMAC Check (100%)", "16"],
                ];
                break;
            default:
                Tasks = [
                    ["MCI Installed", "16"],
                    ["TMM Received for Commissioning", "16"],
                    ["Brake Control", "16"],
                    ["Throttle Control", "16"],
                    ["Cold Commissioning", "16"],
                    ["L9 Activation", "16"],
                    ["SIMAC Activation", "16"],
                ];
                break;
        }
        for (let index = 0; index < Tasks.length; index++) {
            const task = Tasks[index];
            const timestamp = getTimestamp(index);
            try {
                const jsonParam = await ProjectService.SetTaskParam(
                    "",
                    task_id,
                    task[0],
                    "",
                    task[1],
                    timestamp,
                    "active",
                    apiKey
                );
                if (jsonParam.count > 0) {
                    const param_id = jsonParam.task_param[0]?.task_param_id;
                    const jsonVal = await ProjectService.SetParamVal(
                        "",
                        param_id,
                        "0",
                        "active",
                        apiKey
                    );
                    if (jsonVal.count > 0 && index === Tasks.length - 1) {
                        Swal.fire({
                            title: "Success!",
                            showConfirmButton: true,
                            confirmButtonText: "confirm",
                            icon: "success",
                            color: theme.palette.custom.text,
                            background: theme.palette.custom.inputBackgroundColor,
                        }).then(() => {
                            navigate("/machineStatus");
                        });
                    }
                }
            } catch (error) {
                console.log(error);
            }
        }
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setSavingLoading(true);

        const projectId = String(getUrlVars()["projectId"]);
        let urlGroupId = String(getUrlVars()["groupId"]);
        const orgID = organziation?.operation_id;
        const apiKey = organziation?.api_key_list?.[0]?.key;

        if (!name) {
            Swal.fire({
                title: "Missing required fields!",
                icon: "warning",
                color: theme.palette.custom.text,
                background: theme.palette.custom.inputBackgroundColor,
            });
            setSavingLoading(false);
            return;
        }

        const isCreate =
            !urlGroupId || urlGroupId === "undefined" || urlGroupId === "";
        let groupId = urlGroupId;

        try {
            let groupRes;
            if (isCreate) {
                groupRes = await ProjectService.SetProjectGroup(
                    "",
                    projectId,
                    "",
                    name,
                    "",
                    "active",
                    apiKey
                );
                if (groupRes.count > 0) {
                    groupId = groupRes.task_group[0].task_group_id;
                    UpdateURLQueryStringWithoutReload("group_id", groupId);
                } else {
                    throw new Error("Could not create project group.");
                }
            } else {
                groupRes = await ProjectService.SetProjectGroup(
                    groupId,
                    projectId,
                    "",
                    name,
                    "",
                    "active",
                    apiKey
                );
            }
        } catch (error) {
            Swal.fire({ title: "Failed to save group", icon: "error" });
            setSavingLoading(false);
            return;
        }

        let summary = await ProjectService.GetProjectSummary(
            projectId,
            "",
            "active",
            apiKey
        );
        const group =
            summary.count > 0
                ? summary.project_summary.find((g) => g.task_group_id === groupId)
                : null;
        let progressID = "";
        let infoID = "";
        if (group) {
            const progressTask = group.tasks?.find((t) => t.name === "Progress");
            progressID = progressTask?.task_id || "";
            const infoTask = group.tasks?.find((t) => t.name === "Info");
            infoID = infoTask?.task_id || "";
        }

        if (isCreate) {
            if (!progressID) {
                const res = await ProjectService.SetProjectTask(
                    "",
                    projectId,
                    groupId,
                    "",
                    "Progress",
                    "",
                    "active",
                    apiKey
                );
                if (res.count > 0) progressID = res.project_task[0].task_id;
            }
            if (!infoID) {
                const res = await ProjectService.SetProjectTask(
                    "",
                    projectId,
                    groupId,
                    "",
                    "Info",
                    "",
                    "active",
                    apiKey
                );
                if (res.count > 0) infoID = res.project_task[0].task_id;
            }
            if (progressID) {
                await createTasks(progressID, orgID, apiKey);
            }
        }

        if (serialParamID && serialParamID !== "") {
            try {
                await ProjectService.SetParamVal(
                    "",
                    serialParamID,
                    serial,
                    "active",
                    apiKey
                );
            } catch (error) {
                Swal.fire({ title: "Failed to set serial!", icon: "error" });
                setSavingLoading(false);
                return;
            }
        } else {
            if (infoID && infoID !== "") {
                try {
                    const timestamp = getTimestamp(1);
                    const jsonParam = await ProjectService.SetTaskParam(
                        "",
                        infoID,
                        "serial",
                        "",
                        "16",
                        timestamp,
                        "active",
                        apiKey
                    );
                    await ProjectService.SetParamVal(
                        "",
                        jsonParam.task_param[0].task_param_id,
                        serial,
                        "active",
                        apiKey
                    );
                } catch (error) {
                    Swal.fire({ title: "Failed to set serial!", icon: "error" });
                    setSavingLoading(false);
                    return;
                }
            }
        }

        if (commissioningSignoffBase64 && progressID) {
            try {
                await ProjectService.SetProjectFile({
                    operation_id: orgID,
                    project_profile_id: projectId,
                    task_id: progressID,
                    file_type: "pdf",
                    file_name: "commissioning_sign_off",
                    base64_content: commissioningSignoffBase64,
                    apiKey: apiKey,
                });
            } catch (error) {
                console.log(error);
            }
        }

        Swal.fire({
            title: isCreate
                ? "Machine Status Added Successfully!"
                : "Machine Status Updated!",
            icon: "success",
            confirmButtonText: "Confirm",
            color: theme.palette.custom.text,
            background: theme.palette.custom.inputBackgroundColor,
        }).then(() => {
            navigate("/machineStatus");
        });

        setSavingLoading(false);
    }

    function UpdateURLQueryStringWithoutReload(key, value, url) {
        if (!url) url = window.location.href;
        let re = new RegExp("([?&])" + key + "=.*?(&|#|$)(.*)", "gi"),
            hash;
        if (re.test(url)) {
            if (typeof value !== "undefined" && value !== null)
                url = url.replace(re, "$1" + key + "=" + value + "$2$3");
            else {
                hash = url.split("#");
                url = hash[0].replace(re, "$1$3").replace(/(&|\?)$/, "");
                if (typeof hash[1] !== "undefined" && hash[1] !== null)
                    url += "#" + hash[1];
            }
        } else {
            if (typeof value !== "undefined" && value !== null) {
                const separator = url.indexOf("?") !== -1 ? "&" : "?";
                hash = url.split("#");
                url = hash[0] + separator + key + "=" + value;
                if (typeof hash[1] !== "undefined" && hash[1] !== null)
                    url += "#" + hash[1];
            }
        }
        window.history.pushState({ path: url }, "", url);
    }

    function getTimestamp(seconds) {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const timestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        return timestamp;
    }

    function handleRemove() {
        const groupIdDelete = groupId || getUrlVars()["groupId"];
        const projectId = getUrlVars()["projectId"];
        const apiKey = organziation?.api_key_list?.[0]?.key;
        if (!groupIdDelete || groupIdDelete === "undefined") {
            Swal.fire({
                title: "Could not determine machine to delete!",
                icon: "error",
                color: theme.palette.custom.text,
                background: theme.palette.custom.inputBackgroundColor,
            });
            return;
        }
        Swal.fire({
            title: "Are you sure you want to delete this machine?",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            icon: "warning",
            color: theme.palette.custom.text,
            background: theme.palette.custom.inputBackgroundColor,
        }).then((result) => {
            if (result.isConfirmed) {
                ProjectService.SetProjectGroup(
                    groupIdDelete,
                    projectId || "",
                    "",
                    "",
                    "",
                    "removed",
                    apiKey
                )
                    .then((json) => {
                        if (json && json.count > 0) {
                            Swal.fire({
                                title: "Machine deleted!",
                                icon: "success",
                                color: theme.palette.custom.text,
                                background: theme.palette.custom.inputBackgroundColor,
                            }).then(() => {
                                navigate("/machineStatus");
                            });
                        } else {
                            Swal.fire({
                                title: "Failed to delete machine!",
                                text: json?.error_description || "Unknown error.",
                                icon: "error",
                                color: theme.palette.custom.text,
                                background: theme.palette.custom.inputBackgroundColor,
                            });
                        }
                    })
                    .catch((err) => {
                        Swal.fire({
                            title: "API error!",
                            text: err?.message || "Could not reach server.",
                            icon: "error",
                            color: theme.palette.custom.text,
                            background: theme.palette.custom.inputBackgroundColor,
                        });
                    });
            }
        });
    }

    return (
        <Box
            sx={{
                p: "12px",
                borderRadius: "16px",
                border: `1px solid ${theme.palette.custom.borderColor}`,
            }}
            style={{ height: "100%" }}
        >
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Box sx={{ display: { xs: "block", sm: "flex" } }}>
                <Typography
                    sx={{
                        fontSize: "45px",
                        mb: 0,
                        color: theme.palette.custom.borderColor,
                    }}
                    gutterBottom
                >
                    {t("AddMachineStatus")}:
                </Typography>
            </Box>
            <Divider
                sx={{ my: "10px", backgroundColor: theme.palette.custom.borderColor }}
            />
            <Box
                sx={{
                    display: { xs: "block", sm: "flex" },
                    width: "100%",
                    flexDirection: "row",
                    color: "#FFFFFF",
                }}
            >
                <Box
                    sx={{
                        display: { xs: "block", sm: "flex" },
                        width: "95%",
                        flexDirection: "column",
                        color: "#FFFFFF",
                        marginRight: "20px",
                    }}
                >
                    <CustomTextField
                        error={!name}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                        }}
                        id="txtName"
                        label={t("MachineName")}
                        variant="filled"
                        margin="normal"
                    />
                    <CustomTextField
                        id="add_commissioning_sign_off"
                        label={t("Commissioning Sign Off (PDF)")}
                        type="file"
                        variant="filled"
                        margin="normal"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            accept: "application/pdf",
                        }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setCommissioningSignoffFile(file);
                            const reader = new FileReader();
                            reader.onload = (event) => {
                                const result = event.target.result;
                                const base64 = result.split(",")[1];
                                setCommissioningSignoffBase64(base64);
                            };
                            reader.readAsDataURL(file);
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        display: { xs: "block", sm: "flex" },
                        width: "95%",
                        flexDirection: "column",
                        color: "#FFFFFF",
                    }}
                >
                    <CustomTextField
                        error={!serial}
                        value={serial}
                        onChange={(e) => {
                            setSerial(e.target.value);
                        }}
                        id="txtSerial"
                        label={t("MachineSerial")}
                        variant="filled"
                        margin="normal"
                    />
                </Box>
            </Box>
            <Box sx={{ width: "100%", alignItems: "end", textAlign: "end" }}>
                <StyledButton
                    sx={{ mt: 3, mb: 1, mr: 1 }}
                    onClick={() => handleRemove(groupId)}
                >
                    {t("Delete")}
                </StyledButton>
                <StyledLoadingButton
                    loading={savingLoading}
                    loadingIndicator={<CircularProgress color="primary" size={16} />}
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 1 }}
                    onClick={handleSubmit}
                >
                    {t("Save")}
                </StyledLoadingButton>
            </Box>
        </Box>
    );
}

export default AddMachineStatusPage;
